import { LitElement } from 'lit';
import { injectStyleInHead } from '@ids/style-inject';

const IDS_PREVENT_SCROLL_CLASS_NAME = 'ids-prevent-scroll';
const typedGlobalThis = globalThis as unknown as { ELEMENTS_BLOCKING_SCROLL?: Set<HTMLElement> };

injectStyleInHead({
    cssText: `.ids-prevent-scroll {
    position: fixed;
    width: 100%;
}`,
    dataset: {
        id: 'ids-base-lit-element',
        library: 'web-component',
        type: 'styles',
    },
    insertAtTop: true,
});

export type BaseLitElementOptions = {
    /**
     * Enable a MutationObserver to watch for changes in the children subtree of the element
     * and request an update when changes are detected
     * Warning: Enabling this could lead to more frequent component rendering
     */
    isChildrenObserverEnabled?: boolean;

    /**
     * Enable a ResizeObserver to watch for changes in the size of the element
     * and request an update when changes are detected
     * Warning: Enabling this could lead to more frequent component rendering
     */
    isResizeObserverEnabled?: boolean;

    /**
     * Enable a window resize listener to watch for changes in the size of the window
     * and request an update when changes are detected
     * Warning: Enabling this could lead to more frequent component rendering
     */
    isWindowResizeListenerEnabled?: boolean;

    /**
     * Set window resize listener debounce timeout delay in milliseconds, by default 200ms
     */
    windowResizeListenerDebounceTimeoutDelay?: number;
};

export type SlotItem = {
    slotName: string;
    slotElement: HTMLElement;
};

export const BaseLitElementUpdate = Object.freeze({
    CHILDREN: 'BaseLitElementUpdateChildren',
    RESIZE: 'BaseLitElementUpdateSize',
    WINDOW_RESIZE: 'BaseLitElementUpdateWindowSize',
});
export type BaseLitElementUpdate = (typeof BaseLitElementUpdate)[keyof typeof BaseLitElementUpdate];

export type BaseLitElementUpdateChildren = {
    mutations: MutationRecord[];
    observer: MutationObserver;
};

export type BaseLitElementUpdateResize = {
    entries: ResizeObserverEntry[];
    observer: ResizeObserver;
};

export type BaseLitElementUpdateWindowResize = {
    event: UIEvent;
};

export abstract class BaseLitElement extends LitElement {
    protected static uid: number = 1;

    protected readonly uid: number = BaseLitElement.uid;
    protected childrenObserver: MutationObserver | null = null;
    protected resizeObserver: ResizeObserver | null = null;
    protected readonly boundWindowResizeListener = this.windowResizeListener.bind(this);
    protected windowResizeListenerDebounceTimeout: NodeJS.Timeout | null = null;

    static override shadowRootOptions = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    };

    constructor(protected options: BaseLitElementOptions = {}) {
        super();
        this.options = {
            isChildrenObserverEnabled: false,
            isResizeObserverEnabled: false,
            isWindowResizeListenerEnabled: false,
            windowResizeListenerDebounceTimeoutDelay: 200,
            ...options,
        };

        BaseLitElement.uid += 1;
    }

    override connectedCallback(): void {
        super.connectedCallback();

        if (this.options.isChildrenObserverEnabled) {
            this.observeChildrenChanges();
        }
        if (this.options.isResizeObserverEnabled) {
            this.observeResizeChanges();
        }
        if (this.options.isWindowResizeListenerEnabled) {
            window.addEventListener('resize', this.boundWindowResizeListener);
        }
    }

    override disconnectedCallback(): void {
        if (this.options.isWindowResizeListenerEnabled) {
            window.removeEventListener('resize', this.boundWindowResizeListener);
            if (this.windowResizeListenerDebounceTimeout) {
                clearTimeout(this.windowResizeListenerDebounceTimeout);
            }
        }
        this.resizeObserver?.disconnect();
        this.childrenObserver?.disconnect();

        super.disconnectedCallback();
    }

    private observeChildrenChanges() {
        this.childrenObserver = new MutationObserver((mutations, observer) => {
            this.requestUpdate(BaseLitElementUpdate.CHILDREN, {
                mutations,
                observer,
            } satisfies BaseLitElementUpdateChildren);
        });

        this.childrenObserver.observe(this, {
            childList: true, // Detect added/removed children
            attributes: true, // Detect attribute changes
            subtree: true, // Detect changes deep in children
        });
    }

    private observeResizeChanges() {
        this.resizeObserver = new ResizeObserver((entries, observer) => {
            this.requestUpdate(BaseLitElementUpdate.RESIZE, {
                entries,
                observer,
            } satisfies BaseLitElementUpdateResize);
        });

        this.resizeObserver.observe(this);
    }

    private windowResizeListener(event: UIEvent) {
        if (this.windowResizeListenerDebounceTimeout) {
            clearTimeout(this.windowResizeListenerDebounceTimeout);
        }
        this.windowResizeListenerDebounceTimeout = setTimeout(() => {
            this.requestUpdate(BaseLitElementUpdate.WINDOW_RESIZE, {
                event,
            } satisfies BaseLitElementUpdateWindowResize);
        }, this.options.windowResizeListenerDebounceTimeoutDelay);
    }

    /**
     * Find slots by name prefix
     *
     * @protected
     * @param {string} slotNamePrefix
     * @return {SlotItem[]}
     */
    protected findSlotsByNamePrefix(slotNamePrefix: string): SlotItem[] {
        return Array.from(this.children).reduce<SlotItem[]>((slots, slotElement) => {
            const slotName = slotElement.attributes.getNamedItem('slot')?.value;
            if (
                slotElement instanceof HTMLElement &&
                slotName &&
                slotName.startsWith(slotNamePrefix) &&
                !slots.find((slot) => slot.slotName === slotName)
            ) {
                slots.push({ slotName, slotElement });
            }
            return slots;
        }, []);
    }

    /**
     *  Immediately flush pending DOM style/layout changes
     *
     * @protected
     * @returns {Promise<void>}
     */
    protected async forceLayoutReflow(): Promise<void> {
        // Force layout reflow by reading offsetWidth,
        // This is a common trick to force the browser to reflow the layout
        // and apply any pending style changes
        void this.offsetWidth;
        await this.updateComplete;
    }

    /* === Background scroll fix === */
    private static elementsBlockingScroll: Set<HTMLElement>;
    static {
        /**
         * Global variable on the Window object ensures that all
         * separately built and bundled web components share the same ObservableSet instance.
         *
         * Independently bundled components each include their own copy of the BaseLitElement class,
         * resulting in multiple, isolated static fields
         **/
        if (!typedGlobalThis.ELEMENTS_BLOCKING_SCROLL) {
            typedGlobalThis.ELEMENTS_BLOCKING_SCROLL = new Set<HTMLElement>();
        }

        this.elementsBlockingScroll = typedGlobalThis.ELEMENTS_BLOCKING_SCROLL;
    }

    private static handleScrollBlock() {
        if (
            BaseLitElement.elementsBlockingScroll?.size &&
            !document.documentElement.classList.contains(IDS_PREVENT_SCROLL_CLASS_NAME)
        ) {
            document.documentElement.style.top = `-${window.scrollY}px`;
            document.documentElement.classList.add(IDS_PREVENT_SCROLL_CLASS_NAME);
        }

        if (
            !BaseLitElement.elementsBlockingScroll?.size &&
            document.documentElement.classList.contains(IDS_PREVENT_SCROLL_CLASS_NAME)
        ) {
            const scrollY = -parseInt(document.documentElement.style.top, 10);
            document.documentElement.style.top = '';
            document.documentElement.classList.remove(IDS_PREVENT_SCROLL_CLASS_NAME);
            document.documentElement.scrollTop = scrollY;
        }
    }

    public blockBackgroundScroll(component: HTMLElement): void {
        BaseLitElement.elementsBlockingScroll?.add(component);
        BaseLitElement.handleScrollBlock();
    }

    public unblockBackgroundScroll(component: HTMLElement): void {
        BaseLitElement.elementsBlockingScroll?.delete(component);
        BaseLitElement.handleScrollBlock();
    }
}
